import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, Check, X } from 'lucide-react';
import { getIcon, stepLayout } from './announcementContent';
import styles from './AnnouncementModal.module.scss';

const TITLE_ID = 'announcement-modal-title';

const list = (value) => (Array.isArray(value) ? value : []);

// Keys use the index too: Backstage previews half-filled steps, where text
// can be empty or repeated
const Flow = ({ flow }) => (
  <div className={styles.flow} aria-hidden="true">
    <div className={styles.sources}>
      {list(flow.sources).map((source, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`${index}-${source}`} className={styles.source}>
          {source}
        </span>
      ))}
    </div>
    <ArrowRight size={18} className={styles.flowArrow} />
    <span className={styles.flowResult}>{flow.result}</span>
  </div>
);

const Columns = ({ columns }) => (
  <div className={styles.columns}>
    {columns.map((column, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} className={styles.column}>
        <h3 className={styles.columnHeading}>{column.heading}</h3>
        <ul className={styles.columnList}>
          {list(column.items).map((item, itemIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${itemIndex}-${item}`}>
              <Check size={14} aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const Points = ({ points }) => (
  <ul className={styles.points}>
    {points.map((point, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={index} className={styles.point}>
        <span className={styles.pointTitle}>{point.title}</span>
        <span className={styles.pointText}>{point.text}</span>
      </li>
    ))}
  </ul>
);

Flow.propTypes = {
  flow: PropTypes.shape({
    sources: PropTypes.arrayOf(PropTypes.string),
    result: PropTypes.string,
  }).isRequired,
};

Columns.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
};

Points.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
    })
  ).isRequired,
};

/**
 * How the launch modal looks, without any of its behaviour. Used by
 * AnnouncementModal and by the Backstage preview.
 */
const AnnouncementModalView = ({
  announcement,
  step,
  onStepChange,
  onClose,
  onCta,
  onGuide,
  dialogRef,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState(false);

  const modal = announcement.modal || {};
  const steps = list(modal.steps).filter(Boolean);

  if (steps.length === 0) {
    return null;
  }

  const index = step >= 0 && step < steps.length ? step : 0;
  const current = steps[index];
  const layout = stepLayout(current);
  const isLast = index === steps.length - 1;
  const Icon = getIcon(announcement.icon);

  const handleCta = (event) => {
    event.preventDefault();

    if (modal.requires_ack && !acknowledged) {
      setAckError(true);
      return;
    }

    onCta(event);
  };

  return (
    <div className={styles.overlay}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <div className={styles.top}>
          <span className={styles.stepLabel}>
            Step {index + 1} of {steps.length}
          </span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.progress} aria-hidden="true">
          {steps.map((item, itemIndex) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={itemIndex}
              className={`${styles.progressStep} ${
                itemIndex <= index ? styles.progressDone : ''
              }`}
            />
          ))}
        </div>

        {layout === 'flow' && current.flow ? (
          <Flow flow={current.flow} />
        ) : (
          <span className={styles.icon} aria-hidden="true">
            <Icon size={22} />
          </span>
        )}

        <h2 id={TITLE_ID} className={styles.title}>
          {current.title}
        </h2>
        {current.subheading && (
          <p className={styles.subheading}>{current.subheading}</p>
        )}
        {current.body && <p className={styles.body}>{current.body}</p>}
        {layout === 'columns' && Array.isArray(current.columns) && (
          <Columns columns={current.columns.filter(Boolean)} />
        )}
        {layout === 'points' && Array.isArray(current.points) && (
          <Points points={current.points.filter(Boolean)} />
        )}

        {isLast && modal.requires_ack && (
          <div className={styles.ack}>
            <label className={styles.ackLabel}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => {
                  setAcknowledged(event.target.checked);
                  setAckError(false);
                }}
              />
              <span>{modal.ack_text}</span>
            </label>
            {ackError && (
              <span className={styles.ackError} role="alert">
                Tick the box to continue.
              </span>
            )}
          </div>
        )}

        <div className={styles.footer}>
          {index > 0 && (
            <button
              type="button"
              className={styles.secondary}
              onClick={() => onStepChange(index - 1)}
            >
              Back
            </button>
          )}
          {isLast && announcement.help_url && (
            <a
              href={announcement.help_url}
              className={styles.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onGuide}
            >
              Read the guide
            </a>
          )}
          {isLast ? (
            announcement.cta_url && (
              <a
                href={announcement.cta_url}
                className={styles.primary}
                onClick={handleCta}
              >
                {announcement.cta_label}
              </a>
            )
          ) : (
            <button
              type="button"
              className={styles.primary}
              onClick={() => onStepChange(index + 1)}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

AnnouncementModalView.propTypes = {
  announcement: PropTypes.shape({
    icon: PropTypes.string,
    cta_label: PropTypes.string,
    cta_url: PropTypes.string,
    help_url: PropTypes.string,
    modal: PropTypes.shape({
      requires_ack: PropTypes.bool,
      ack_text: PropTypes.string,
      steps: PropTypes.arrayOf(PropTypes.object),
    }),
  }).isRequired,
  step: PropTypes.number,
  onStepChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCta: PropTypes.func.isRequired,
  onGuide: PropTypes.func,
  dialogRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

AnnouncementModalView.defaultProps = {
  step: 0,
  onGuide: undefined,
  dialogRef: undefined,
};

export default AnnouncementModalView;
